import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { SurveysContext } from './SurveysContext'
import * as Components from '../components'
import * as Config from '../config'
import * as GQL from '../gql'
import { useMutation } from '@apollo/client'
import { useStatsigClient } from '@/src/services/Statsig/PlatformStatsig'

export const SurveysProvider = ({ children }: { children: React.ReactNode }) => {
  const isSurveyScheduledForPresentationRef = useRef(false)
  const { client: statsigClient } = useStatsigClient()
  const { data, query } = GQL.useGetAvailableSurveys()
  const [recordSurveyResponse] = useMutation(GQL.RECORD_SURVEY_RESPONSE)
  const availableSurveysRef = useRef<Config.SurveyId[]>([])
  const [surveyToShow, setSurveyToShow] = useState<{
    surveyId: Config.SurveyId
    trigger: Config.Trigger
  } | null>(null)

  useEffect(() => {
    console.log('Updating available surveys', data?.availableSurveys.items)
    // When the available surveys change, update the ref.
    availableSurveysRef.current =
      data?.availableSurveys.items
        .map((survey) => Config.surveyIdSchema.safeParse(survey.id))
        .filter((result) => result.success)
        .map((result) => result.data) || []
    console.log('Available surveys updated to', availableSurveysRef.current)
  }, [data])

  const evaluateTrigger = useCallback(
    async (trigger: Config.Trigger) => {
      if (isSurveyScheduledForPresentationRef.current) {
        console.log('Survey already scheduled for presentation, skipping', trigger)
        return
      }

      const surveysForTrigger = availableSurveysRef.current.filter((survey) =>
        Config.Configuration[survey].validTriggers.some((t) => t === trigger),
      )

      let nextSurvey: Config.SurveyId | null = null

      for (const survey of surveysForTrigger) {
        const isSurveyEnabled =
          !Config.Configuration[survey].statsigFeatureGate ||
          statsigClient.getFeatureGate(Config.Configuration[survey].statsigFeatureGate).value

        if (isSurveyEnabled) {
          nextSurvey = survey
          break
        }

        console.log('Survey is not enabled via feature gate, skipping', survey)
      }

      if (!nextSurvey) {
        console.log('No survey found for trigger', trigger)
        return
      }

      isSurveyScheduledForPresentationRef.current = true
      console.log('Showing survey after a brief delay', nextSurvey)
      // Delay the survey presentation to allow for transitions.
      await new Promise<void>((resolve) =>
        setTimeout(() => {
          setSurveyToShow({ surveyId: nextSurvey, trigger })
          resolve()
        }, 500),
      )
    },
    [statsigClient],
  )

  const submitSurvey = useCallback(
    async (surveyResults: GQL.SurveyResponse) => {
      console.log('Submitting survey', surveyResults)
      setSurveyToShow(null)
      isSurveyScheduledForPresentationRef.current = false

      try {
        await recordSurveyResponse({
          variables: surveyResults,
          refetchQueries: [query],
          awaitRefetchQueries: true,
        })

        console.log('Survey result submitted')
      } catch (err) {
        console.error('Error submitting survey', err)
      }
    },
    [recordSurveyResponse, query],
  )

  const value = useMemo(
    () => ({
      evaluateTrigger,
      submitSurvey,
    }),
    [evaluateTrigger, submitSurvey],
  )

  return (
    <SurveysContext.Provider value={value}>
      {children}
      {surveyToShow && (
        <Components.SurveyPresenter
          surveyId={surveyToShow.surveyId}
          trigger={surveyToShow.trigger}
        />
      )}
    </SurveysContext.Provider>
  )
}
